import React from 'react';
import { RefreshCw, Trash2, X, CheckSquare, Square, Minus } from 'lucide-react';
import ThemedCard from '../../themed/ThemedCard';
import { RSVPResponseWithTable } from '../types';

interface DeclinedTabProps {
  filteredDeclined: RSVPResponseWithTable[];
  searchTerm: string;
  batchEditMode: boolean;
  selectedItems: Set<string>;
  toggleSelectAll: (items: RSVPResponseWithTable[]) => void;
  getSelectAllState: (items: RSVPResponseWithTable[]) => 'none' | 'all' | 'partial';
  toggleSelectItem: (id: string) => void;
  handleDeleteRSVP: (rsvpId: string, guestName: string) => void;
  deletingRSVPId: string | null;
}

export const DeclinedTab: React.FC<DeclinedTabProps> = ({
  filteredDeclined,
  searchTerm,
  batchEditMode,
  selectedItems,
  toggleSelectAll,
  getSelectAllState,
  toggleSelectItem,
  handleDeleteRSVP,
  deletingRSVPId,
}) => {
  return (
    <ThemedCard className="p-6">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-wedding-text-title mb-2">不出席者列表</h3>
          <p className="text-wedding-text-body">共有 {filteredDeclined.length} 位賓客無法出席</p>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-wedding-pink/10">
          <thead className="bg-wedding-pink/5">
            <tr>
              {batchEditMode && (
                <th className="px-6 py-3 text-left text-xs font-medium text-wedding-text-title uppercase tracking-wider w-12">
                  <button
                    onClick={() => toggleSelectAll(filteredDeclined)}
                    className="flex items-center justify-center w-5 h-5 text-gray-400 hover:text-wedding-pink"
                  >
                    {(() => {
                      const selectState = getSelectAllState(filteredDeclined);
                      if (selectState === 'all') return <CheckSquare className="h-4 w-4 text-wedding-pink" />;
                      if (selectState === 'partial') return <Minus className="h-4 w-4 text-wedding-pink" />;
                      return <Square className="h-4 w-4" />;
                    })()}
                  </button>
                </th>
              )}
              <th className="px-6 py-3 text-left text-xs font-medium text-wedding-text-title uppercase tracking-wider">
                賓客資訊
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-wedding-text-title uppercase tracking-wider">
                不出席原因
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-wedding-text-title uppercase tracking-wider">
                操作
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-wedding-pink/10">
            {filteredDeclined.map((response) => (
              <tr key={response.id} className="hover:bg-wedding-pink/5">
                {batchEditMode && (
                  <td className="px-6 py-4">
                    <button
                      onClick={() => toggleSelectItem(response.id)}
                      className="flex items-center justify-center w-5 h-5 text-gray-400 hover:text-wedding-pink"
                    >
                      {selectedItems.has(response.id) ? (
                        <CheckSquare className="h-4 w-4 text-wedding-pink" />
                      ) : (
                        <Square className="h-4 w-4" />
                      )}
                    </button>
                  </td>
                )}
                <td className="px-6 py-4">
                  <div>
                    <div className="text-sm font-medium text-wedding-text-title">{response.guestName}</div>
                    <div className="text-sm text-wedding-text-body">{response.guestEmail}</div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  {response.rsvpData.message ? (
                    <div className="text-sm text-gray-700 max-w-xs">
                      <p className="truncate" title={response.rsvpData.message}>
                        {response.rsvpData.message}
                      </p>
                    </div>
                  ) : (
                    <span className="text-gray-400">無留言</span>
                  )}
                </td>
                <td className="px-6 py-4">
                  <button
                    onClick={() => handleDeleteRSVP(response.id, response.guestName)}
                    disabled={deletingRSVPId === response.id}
                    className="text-red-400 hover:text-red-600 disabled:opacity-50"
                    title="刪除 RSVP 回覆"
                  >
                    {deletingRSVPId === response.id ? (
                      <RefreshCw className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filteredDeclined.length === 0 && (
        <div className="text-center py-12">
          <X className="h-12 w-12 text-wedding-text-body mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">沒有找到不出席者</h3>
          <p className="text-gray-600">
            {searchTerm ? '調整搜尋條件以查看更多結果' : '目前沒有人表示無法出席'}
          </p>
        </div>
      )}
    </ThemedCard>
  );
};
